import type { IntlShape } from "@formatjs/intl";
import { Card, Flex, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import type { FieldPresence, FieldPresenceStateEnum } from "../../generated-axios-client";
import styles from "../Dashboard.module.css";

type Props = {
  intl: IntlShape;
  rows: FieldPresence[] | undefined;
  loading: boolean;
  onOpenWorkOrder: (ticketId: string) => void;
};

const STATE_TAG_COLOR: Record<FieldPresenceStateEnum, string> = {
  FORGOTTEN_CHECK_IN: "error",
  CHECKED_IN: "success",
  CHECKED_OUT: "default",
};

const STATE_COUNT_MESSAGE: Record<FieldPresenceStateEnum, string> = {
  FORGOTTEN_CHECK_IN: "dashboard_field_forgotten_count",
  CHECKED_IN: "dashboard_field_checked_in_count",
  CHECKED_OUT: "dashboard_field_checked_out_count",
};

// A session left open since an earlier day shows its date as well.
const formatSessionTime = (intl: IntlShape, value: string) =>
  intl.formatMessage(
    { id: dayjs(value).isSame(dayjs(), "day") ? "time" : "full_date" },
    { value: new Date(value) }
  );

const stateLabel = (intl: IntlShape, row: FieldPresence) => {
  if (row.state === "FORGOTTEN_CHECK_IN" && row.checked_in_at) {
    return intl.formatMessage(
      { id: "dashboard_field_state_forgotten_check_in" },
      { time: formatSessionTime(intl, row.checked_in_at) }
    );
  }
  if (row.state === "CHECKED_IN" && row.checked_in_at) {
    return intl.formatMessage(
      { id: "dashboard_field_state_checked_in" },
      { time: formatSessionTime(intl, row.checked_in_at) }
    );
  }
  if (row.state === "CHECKED_OUT" && row.checked_out_at) {
    return intl.formatMessage(
      { id: "dashboard_field_state_checked_out" },
      { time: formatSessionTime(intl, row.checked_out_at) }
    );
  }
  return intl.formatMessage({
    id: `dashboard_field_state_${row.state.toLowerCase()}`,
  });
};

export const TodayInFieldCard = ({ intl, rows, loading, onOpenWorkOrder }: Props) => {
  const countByState: Partial<Record<FieldPresenceStateEnum, number>> = {};
  for (const row of rows ?? []) {
    countByState[row.state] = (countByState[row.state] ?? 0) + 1;
  }

  const columns: ColumnsType<FieldPresence> = [
    {
      key: "technician",
      title: intl.formatMessage({ id: "dashboard_field_column_technician" }),
      render: (_, row) => row.technician_name,
    },
    {
      key: "work_order",
      title: intl.formatMessage({ id: "dashboard_field_column_work_order" }),
      render: (_, row) => (
        <Typography.Link onClick={() => onOpenWorkOrder(row.ticket_id)}>
          {`#${row.ticket_number}`}
          {row.ticket_name ? ` ${row.ticket_name}` : ""}
        </Typography.Link>
      ),
    },
    {
      key: "site",
      title: intl.formatMessage({ id: "dashboard_field_column_site" }),
      render: (_, row) =>
        [row.location_name, row.turbine_serial_number].filter(Boolean).join(" · ") || (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      key: "state",
      title: intl.formatMessage({ id: "dashboard_field_column_status" }),
      render: (_, row) => (
        <Tag color={STATE_TAG_COLOR[row.state]}>{stateLabel(intl, row)}</Tag>
      ),
    },
  ];

  return (
    <Card
      className={styles.fullHeight}
      title={intl.formatMessage({ id: "dashboard_field_title" })}
      extra={
        <Flex gap="small" wrap>
          {(Object.keys(STATE_COUNT_MESSAGE) as FieldPresenceStateEnum[])
            .filter((state) => countByState[state])
            .map((state) => (
              <Tag key={state} color={STATE_TAG_COLOR[state]}>
                {intl.formatMessage(
                  { id: STATE_COUNT_MESSAGE[state] },
                  { count: countByState[state] }
                )}
              </Tag>
            ))}
        </Flex>
      }
    >
      <Table<FieldPresence>
        rowKey={(row) => `${row.technician_id}:${row.ticket_id}`}
        size="small"
        columns={columns}
        dataSource={rows ?? []}
        loading={loading}
        pagination={{ pageSize: 8, hideOnSinglePage: true, size: "small" }}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: intl.formatMessage({ id: "dashboard_field_empty" }) }}
      />
    </Card>
  );
};
